/**
 * Keep an aborted view transition from surfacing as an unhandled rejection.
 *
 * Chrome aborts a view transition whose DOM-update callback has not settled
 * after ~4s and rejects the transition's `ready`, `updateCallbackDone` and
 * `finished` promises with `DOMException: TimeoutError`. `next-view-transitions`
 * starts every navigation transition without touching those promises, so a
 * navigation that waits on a slow route (or a 500) turns into three unhandled
 * rejections that PostHog reports as application errors.
 *
 * The abort itself is expected browser behaviour and there is nothing to
 * recover: the navigation still completes, only unanimated. So the guard
 * swallows exactly the abort and leaves every other rejection alone, so a real
 * render failure still reaches error tracking.
 */

/** The subset of `ViewTransition` the guard has to settle. */
export type ViewTransitionLike = {
  ready?: Promise<void>
  updateCallbackDone?: Promise<void>
  finished?: Promise<void>
}

export type StartViewTransition = (update: () => unknown) => ViewTransitionLike

/** The subset of `document` the guard reads. Optional: Safari has no API. */
export type ViewTransitionDocument = {
  startViewTransition?: StartViewTransition
}

// `TimeoutError` is the DOM-update timeout; `AbortError` is the transition
// being skipped (a second navigation starting, `skipTransition()`).
const ABORT_ERROR_NAMES: ReadonlySet<string> = new Set([
  "TimeoutError",
  "AbortError",
])

const GUARDED_DOCUMENTS = new WeakSet<ViewTransitionDocument>()

// A rejection reason is genuinely untyped, so `unknown` is the honest input here.
function isTransitionAbort(reason: unknown): boolean {
  return reason instanceof DOMException && ABORT_ERROR_NAMES.has(reason.name)
}

function settleQuietly(promise: Promise<void> | undefined): void {
  promise?.catch((reason: unknown) => {
    if (isTransitionAbort(reason)) return
    // Not our abort: hand it back to the runtime so error tracking still sees it.
    throw reason
  })
}

/**
 * Wrap `document.startViewTransition` so transition aborts stay silent.
 *
 * Idempotent per document. Returns a function restoring the original, for an
 * effect cleanup. No-op on a browser without the View Transition API.
 */
export function guardViewTransitions(doc: ViewTransitionDocument): () => void {
  const original = doc.startViewTransition
  if (!original || GUARDED_DOCUMENTS.has(doc)) {
    return () => {}
  }

  doc.startViewTransition = (update: () => unknown) => {
    const transition = original.call(doc, update)
    settleQuietly(transition?.ready)
    settleQuietly(transition?.updateCallbackDone)
    settleQuietly(transition?.finished)
    return transition
  }
  GUARDED_DOCUMENTS.add(doc)

  return () => {
    doc.startViewTransition = original
    GUARDED_DOCUMENTS.delete(doc)
  }
}
